import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { KEYS, readJson, writeJson } from './storage';
import { SEED_VERSION } from '@/fixtures/scenarios';

const StoreContext = createContext(null);
const EMPTY = { statuses: {}, annotations: [], simulations: [] };

/**
 * Vedvarende demohandlinger (kommentarer, avviksstatus, tiltakssimuleringer) per scenario og seedversjon.
 * Seeddata overskrives aldri.
 */
export function DemoStoreProvider({ scenarioId, children }) {
  const key = KEYS.store(scenarioId, SEED_VERSION);
  const [store, setStore] = useState(() => readJson(window.localStorage, key, EMPTY));

  useEffect(() => {
    setStore(readJson(window.localStorage, key, EMPTY));
  }, [key]);

  const update = useCallback(
    (fn) => {
      setStore((prev) => {
        const next = fn(prev);
        writeJson(window.localStorage, key, next);
        return next;
      });
    },
    [key],
  );

  const addComment = useCallback(
    ({ targetId, text, demoAuthor, role, now }) =>
      update((prev) => ({
        ...prev,
        annotations: [
          ...prev.annotations,
          { id: `ann-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, deviationId: targetId, text, demoAuthor, role, createdAt: now, kind: 'comment' },
        ],
      })),
    [update],
  );

  const setStatus = useCallback(
    ({ deviationId, status, demoAuthor, role, now }) =>
      update((prev) => {
        const from = prev.statuses[deviationId] ?? 'open';
        if (from === status) return prev;
        return {
          ...prev,
          statuses: { ...prev.statuses, [deviationId]: status },
          annotations: [
            ...prev.annotations,
            { id: `ann-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, deviationId, text: '', demoAuthor, role, createdAt: now, kind: 'status_change', fromStatus: from, toStatus: status },
          ],
        };
      }),
    [update],
  );

  const addSimulation = useCallback((sim) => update((prev) => ({ ...prev, simulations: [...prev.simulations, sim] })), [update]);
  const removeSimulation = useCallback((id) => update((prev) => ({ ...prev, simulations: prev.simulations.filter((s) => s.id !== id) })), [update]);

  const clear = useCallback(() => {
    window.localStorage.removeItem(key);
    setStore(EMPTY);
  }, [key]);

  const value = useMemo(
    () => ({
      statuses: store.statuses,
      annotations: store.annotations,
      simulations: store.simulations,
      statusOf: (id) => store.statuses[id] ?? 'open',
      annotationsFor: (id) => store.annotations.filter((a) => a.deviationId === id),
      addComment,
      setStatus,
      addSimulation,
      removeSimulation,
      clear,
    }),
    [store, addComment, setStatus, addSimulation, removeSimulation, clear],
  );
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useDemoStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useDemoStore utenfor DemoStoreProvider');
  return ctx;
}
